import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((item, selectedSize = 'default', quantity = 1) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(
        i => i.id === item.id && i.selectedSize === selectedSize
      );
      
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }
      
      const price = item.prices[selectedSize] || item.prices['default'] || Object.values(item.prices)[0];
      
      return [...prev, {
        ...item,
        selectedSize,
        quantity,
        unitPrice: price
      }];
    });
  }, []);

  const removeItem = useCallback((itemId, selectedSize) => {
    setItems(prev => prev.filter(
      i => !(i.id === itemId && i.selectedSize === selectedSize)
    ));
  }, []);

  const updateQuantity = useCallback((itemId, selectedSize, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(itemId, selectedSize);
      return;
    }
    
    setItems(prev => prev.map(i => 
      i.id === itemId && i.selectedSize === selectedSize
        ? { ...i, quantity: newQuantity }
        : i
    ));
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  const toggleCart = () => setIsOpen(prev => !prev);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      totalItems,
      totalPrice,
      isOpen,
      openCart,
      closeCart,
      toggleCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
